import React, { useCallback, useEffect, useState } from 'react';
import { Edit, Trash2 } from 'lucide-react';
import { adminSubscriptionsAPI } from '../../../lib/api';
import { formatSignedMoney, normalizeDecimal, todayISO } from '../../../lib/subscriptionBilling';
import { useToast } from '../../../hooks/use-toast';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';

const emptyForm = () => ({
  payment_date: todayISO(),
  client_id: '',
  amount_rub: '',
});

function formatDate(iso) {
  if (!iso || !iso.includes('-')) return iso || '';
  const [year, month, day] = iso.split('-');
  return `${day}.${month}.${year}`;
}

const SubscriptionPayments = () => {
  const { toast } = useToast();
  const [payments, setPayments] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [paymentRows, clientRows] = await Promise.all([
        adminSubscriptionsAPI.getPayments(),
        adminSubscriptionsAPI.getClients(),
      ]);
      setPayments(paymentRows || []);
      setClients(clientRows || []);
    } catch (error) {
      toast({ title: 'Ошибка загрузки', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedClient = clients.find((item) => String(item.id) === String(form.client_id));

  const reset = () => {
    setEditingId(null);
    setForm(emptyForm());
  };

  const save = async (event) => {
    event.preventDefault();
    if (!form.client_id) {
      toast({ title: 'Выберите клиента', variant: 'destructive' });
      return;
    }
    if (!form.amount_rub.trim()) {
      toast({ title: 'Укажите платёж в рублях', variant: 'destructive' });
      return;
    }
    try {
      await adminSubscriptionsAPI.savePayment(editingId, {
        client_id: Number(form.client_id),
        payment_date: form.payment_date,
        amount_rub: normalizeDecimal(form.amount_rub),
      });
      toast({ title: 'Платёж сохранён' });
      reset();
      await load();
    } catch (error) {
      toast({ title: 'Не сохранилось', description: error.message, variant: 'destructive' });
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Переместить платёж ${formatDate(row.payment_date)} на ${formatSignedMoney(row.amount_rub)} в корзину?`)) return;
    try {
      await adminSubscriptionsAPI.deletePayment(row.id);
      if (editingId === row.id) reset();
      await load();
    } catch (error) {
      toast({ title: 'Не удалось удалить', description: error.message, variant: 'destructive' });
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
      <Card>
        <CardHeader>
          <CardTitle>{editingId ? 'Платёж' : 'Новый платёж'}</CardTitle>
          <CardDescription>Входящий платёж увеличивает баланс клиента. Списания в рублях его уменьшают.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={save} className="space-y-3">
            <div className="space-y-2">
              <Label>Дата платежа</Label>
              <Input type="date" value={form.payment_date} onChange={(event) => setForm((prev) => ({ ...prev, payment_date: event.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Клиент</Label>
              <Select value={form.client_id || undefined} onValueChange={(value) => setForm((prev) => ({ ...prev, client_id: value }))}>
                <SelectTrigger><SelectValue placeholder="Выберите клиента" /></SelectTrigger>
                <SelectContent>
                  {clients.filter((item) => item.is_active || String(item.id) === String(form.client_id)).map((item) => (
                    <SelectItem key={item.id} value={String(item.id)}>{item.name} · {item.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Платёж в рублях</Label>
              <Input
                inputMode="decimal"
                value={form.amount_rub}
                onChange={(event) => setForm((prev) => ({ ...prev, amount_rub: event.target.value }))}
                placeholder="0,00"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              Баланс клиента: {selectedClient ? formatSignedMoney(selectedClient.balance || '0') : '—'}
            </p>
            <div className="flex gap-2">
              <Button type="submit">Сохранить</Button>
              {editingId && <Button type="button" variant="outline" onClick={reset}>Отмена</Button>}
            </div>
          </form>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Дата платежа</TableHead>
                <TableHead>Клиент</TableHead>
                <TableHead>Компания</TableHead>
                <TableHead>Платёж в рублях</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={5}>Загрузка...</TableCell></TableRow>}
              {!loading && payments.length === 0 && <TableRow><TableCell colSpan={5}>Платежей нет.</TableCell></TableRow>}
              {!loading && payments.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{formatDate(row.payment_date)}</TableCell>
                  <TableCell>{row.client_name}</TableCell>
                  <TableCell>{row.company_name}</TableCell>
                  <TableCell>{formatSignedMoney(row.amount_rub)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      aria-label="Исправить"
                      onClick={() => {
                        setEditingId(row.id);
                        setForm({
                          payment_date: row.payment_date,
                          client_id: String(row.client_id),
                          amount_rub: row.amount_rub,
                        });
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button type="button" size="icon" variant="ghost" aria-label="Удалить" onClick={() => remove(row)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionPayments;
